import { parsePhoneNumberFromString, CountryCode } from "libphonenumber-js";
import { loadStripe } from "@stripe/stripe-js";
import toast from "react-hot-toast";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY || "");

export const formatPhoneNumber = (phoneNumber: string, countryCode: string) => {
  const phoneNumberObj = parsePhoneNumberFromString(phoneNumber, countryCode as CountryCode);
  return phoneNumberObj ? phoneNumberObj.format("E.164") : null;
};

export const createParticularStripeToken = async (data: any) => {
  if (!stripePromise) {
    toast.error("Stripe n'est pas configuré correctement");
    throw new Error("Stripe not configured");
  }

  try {
    const stripe = await stripePromise;
    if (!stripe) {
      toast.error("Impossible de charger Stripe");
      throw new Error("Stripe failed to load");
    }

    // Validation des données requises
    if (!data?.Client?.phone) {
      toast.error("Numéro de téléphone manquant");
      throw new Error("Phone number is required");
    }

    if (!data?.Client?.name || !data?.Client?.lastName) {
      toast.error("Nom et prénom requis");
      throw new Error("First name and last name are required");
    }

    if (!data?.Client?.dateOfBirth) {
      toast.error("Date de naissance manquante");
      throw new Error("Date of birth is required");
    }

    const phone_number = formatPhoneNumber(data?.Client?.phone, "FR");
    if (!phone_number) {
      toast.error(`Numéro de téléphone invalide: ${data?.Client?.phone}`);
      throw new Error("Invalid phone number format");
    }

    const date = new Date(data?.Client.dateOfBirth);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();

    const { token, error } = await stripe.createToken("account", {
      individual: {
        first_name: data?.Client?.name,
        last_name: data?.Client?.lastName,
        email: data?.Client?.User?.email,
        phone: phone_number,
        dob: {
          day: day,
          month: month,
          year: year,
        },
        address: {
          city: data?.Client?.city,
          line1: data?.Client?.line1,
          line2: data?.Client?.rue,
          postal_code: data?.Client?.postal_code,
        },
      },
      tos_shown_and_accepted: true,
      business_type: "individual",
    });

    if (error) {
      toast.error(`Erreur Stripe: ${error.message}`);
      throw new Error(`Stripe API error: ${error.message}`);
    }

    if (!token?.id) {
      toast.error("Token Stripe non généré");
      throw new Error("Token ID is missing");
    }

    return token.id;
  } catch (error: any) {
    console.error("Error in createParticularStripeToken:", error);
    toast.error(`Erreur création token Stripe: ${error.message}`);
    throw new Error(`Token creation failed: ${error.message}`);
  }
};

export const createBankAccountToken = async (name: string, type: string, rib: string) => {
  if (!stripePromise) return null;

  try {
    const stripe = await stripePromise;
    if (stripe) {
      const { token, error } = await stripe.createToken("bank_account", {
        country: "FR",
        currency: "EUR",
        account_holder_name: name,
        account_holder_type: type,
        account_number: rib,
      });
      if (error) {
        return null;
      } else {
        return token?.id;
      }
    }
  } catch (error: any) {
    toast.error(`Error in createBankAccountToken:  ${error.message}`);
    throw new Error("Bank Account Token creation failed");
  }
};

export const createCompanyStripeToken = async (data: any) => {
  if (!stripePromise) {
    toast.error("Stripe n'est pas configuré correctement");
    throw new Error("Stripe not configured");
  }

  try {
    const stripe = await stripePromise;
    if (!stripe) {
      toast.error("Impossible de charger Stripe");
      throw new Error("Stripe failed to load");
    }

    // Validation des données requises
    if (!data?.Client?.companyPhone) {
      toast.error("Numéro de téléphone de l'entreprise manquant");
      throw new Error("Company phone number is required");
    }

    if (!data?.Client?.companySiren) {
      toast.error("SIREN de l'entreprise manquant");
      throw new Error("Company SIREN is required");
    }

    if (!data?.Client?.companyName) {
      toast.error("Nom de l'entreprise manquant");
      throw new Error("Company name is required");
    }

    const phone_number = formatPhoneNumber(data?.Client?.companyPhone, "FR");
    if (!phone_number) {
      toast.error(`Numéro de téléphone invalide: ${data?.Client?.companyPhone}`);
      throw new Error("Invalid phone number format");
    }

    const { token, error } = await stripe.createToken("account", {
      company: {
        directors_provided: true,
        owners_provided: true,
        executives_provided: true,
        tax_id: data?.Client?.companySiren,
        name: data?.Client?.companyName,
        phone: phone_number,
        address: {
          city: data?.Client?.city,
          line1: data?.Client?.line1,
          line2: data?.Client?.rue,
          postal_code: data?.Client?.postal_code,
          country: "FR",
        },
      },
      tos_shown_and_accepted: true,
      business_type: "company",
    });

    if (error) {
      toast.error(`Erreur Stripe: ${error.message}`);
      throw new Error(`Stripe API error: ${error.message}`);
    }

    if (!token?.id) {
      toast.error("Token Stripe non généré");
      throw new Error("Token ID is missing");
    }

    return token.id;
  } catch (error: any) {
    console.error("Error in createCompanyStripeToken:", error);
    toast.error(`Erreur création token Stripe: ${error.message}`);
    throw new Error(`Token creation failed: ${error.message}`);
  }
};

/* Connected account by ADMIN Dashboard  */
export const createParticularStripeTokenMethod = async (data: any) => {
  if (!stripePromise) return null;

  try {
    const stripe = await stripePromise;
    if (stripe) {
      const phone_number = formatPhoneNumber(data?.Clients?.[0]?.phone, "FR");
      if (!phone_number) {
        return null;
      }

      const date = new Date(data?.Clients[0]?.dateOfBirth);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const day = date.getDate();

      const { token, error } = await stripe.createToken("account", {
        individual: {
          first_name: data?.Clients?.[0]?.name,
          last_name: data?.Clients?.[0]?.lastName,
          email: data?.email,
          phone: phone_number,
          dob: {
            day: day,
            month: month,
            year: year,
          },
          address: {
            city: data?.city,
            line1: data?.address,
            line2: data?.address2,
            postal_code: (data?.codePostale).toString(),
          },
        },
        tos_shown_and_accepted: true,
        business_type: "individual",
      });
      if (error) {
        toast.error(`Error creating Stripe token: ${error.message}`);
        return null;
      } else {
        return token?.id;
      }
    }
  } catch (error: any) {
    toast.error(`Error in createParticularStripeToken:  ${error.message}`);
    throw new Error("Token creation failed");
  }
};

export const createBankAccountTokenMethod = async (name: string, type: string, rib: string) => {
  if (!stripePromise) return null;

  try {
    const stripe = await stripePromise;
    if (stripe) {
      const { token, error } = await stripe.createToken("bank_account", {
        country: "FR",
        currency: "EUR",
        account_holder_name: name,
        account_holder_type: type,
        account_number: rib,
      });
      if (error) {
        toast.error(`Error creating Stripe Bank Account Token: ${error.message}`);
        return null;
      } else {
        return token?.id;
      }
    }
  } catch (error: any) {
    toast.error(`Error in createBankAccountToken:  ${error.message}`);
    throw new Error("Bank Account Token creation failed");
  }
};

export const createCompanyStripeTokenMethod = async (data: any) => {
  if (!stripePromise) return null;

  try {
    const stripe = await stripePromise;
    if (stripe) {
      const phone_number = formatPhoneNumber(data?.companyPhone, "FR");
      if (!phone_number) {
        return null;
      }
      const { token, error } = await stripe.createToken("account", {
        company: {
          directors_provided: true,
          owners_provided: true,
          executives_provided: true,
          tax_id: data?.Clients[0]?.companySiren,
          name: data?.Clients[0]?.companyName,
          phone: phone_number,
          address: {
            city: data?.city,
            line1: data?.address,
            line2: data?.address2,
            postal_code: (data?.codePostale).toString(),
            country: "FR",
          },
        },
        tos_shown_and_accepted: true,
        business_type: "company",
      });
      if (error) {
        toast.error(`Error creating Stripe token: ${error.message}`);
        return null;
      } else {
        return token?.id;
      }
    }
  } catch (error: any) {
    toast.error(`Error in createCompanyStripeToken:  ${error.message}`);
    throw new Error("Token creation failed");
  }
};
